import { format } from 'date-fns';
import { LIVESTREAMS, STREAM_POOL } from './data/livestreams';
import { initWorldMap, type WorldMapApi } from './map/world-map';
import { MIGRATION_MAP_FEATURES } from './data/map-features';
import {
  MIGRATION_ROUTE_GROUPS,
  getMigrationGroupById,
  routeWaypointsBounds,
} from './data/migration-routes';
import { getSeasonBadge, getSeasonalState } from './data/migration-seasonality';
import {
  fetchObservationsByTaxonInBounds,
  fetchTaxonDefaultPhotoUrl,
} from './data/inaturalist';
import {
  loadSavedLocation,
  saveUserLocation,
  clearSavedLocation,
  requestBrowserLocation,
} from './location/user-region';
import {
  renderMotherNatureCard,
  renderThreatRow,
  renderMoonTarotCard,
  renderHoroscopeCard,
  renderMeteorsCard,
} from './components';

/** First four streams fill the bottom dock (25% width each). */
const DOCK_STREAMS = LIVESTREAMS.slice(0, 4);
const BACKUP_YT_IDS = ['ydYDqZQpim8', 'F0GOOP82094', 'qpukdDslCjk', 'B4-L2nfGcuE'];

const STREAM_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

const youtubeEmbeddableCache = new Map<string, boolean>();

function escHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function youtubeSrc(id: string, autoplay: boolean): string {
  const p = new URLSearchParams({
    rel: '0',
    mute: '1',
    playsinline: '1',
    autoplay: autoplay ? '1' : '0',
    controls: '0',
    modestbranding: '1',
    disablekb: '1',
    fs: '0',
    iv_load_policy: '3',
    cc_load_policy: '0',
    enablejsapi: '1',
  });
  if (typeof window !== 'undefined' && window.location?.origin) {
    p.set('origin', window.location.origin);
  }
  return `https://www.youtube.com/embed/${id}?${p}`;
}

type YoutubeIFramePlayer = { destroy: () => void };

type YoutubePlayerCtor = new (
  el: HTMLIFrameElement,
  opts: {
    events?: {
      onReady?: () => void;
      onError?: (e: { data: number }) => void;
    };
  }
) => YoutubeIFramePlayer;

let ytIframeApiLoadPromise: Promise<void> | null = null;

function ensureYoutubeIframeApiLoaded(): Promise<void> {
  const w = window as unknown as {
    YT?: { Player?: YoutubePlayerCtor };
    onYouTubeIframeAPIReady?: () => void;
  };
  if (w.YT?.Player) return Promise.resolve();

  ytIframeApiLoadPromise ??= new Promise((resolve) => {
    let settled = false;
    let pollIv = 0;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      if (pollIv) window.clearInterval(pollIv);
      resolve();
    };

    const prev = w.onYouTubeIframeAPIReady;
    w.onYouTubeIframeAPIReady = (): void => {
      try {
        prev?.();
      } finally {
        finish();
      }
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }

    pollIv = window.setInterval(() => {
      if (w.YT?.Player) finish();
    }, 48);
    window.setTimeout(finish, 12_000);
  });

  return ytIframeApiLoadPromise;
}

async function isYoutubeVideoEmbeddable(
  youtubeId: string,
  opts?: { timeoutMs?: number }
): Promise<boolean> {
  const timeoutMs = opts?.timeoutMs ?? 2500;
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const url = `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(
      `https://www.youtube.com/watch?v=${youtubeId}`
    )}`;
    const res = await fetch(url, { signal: ctrl.signal, mode: 'cors' });
    return res.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(t);
  }
}

async function isYoutubeVideoEmbeddableCached(
  youtubeId: string,
  opts?: { timeoutMs?: number }
): Promise<boolean> {
  const cached = youtubeEmbeddableCache.get(youtubeId);
  if (cached !== undefined) return cached;
  const ok = await isYoutubeVideoEmbeddable(youtubeId, opts);
  youtubeEmbeddableCache.set(youtubeId, ok);
  return ok;
}

async function fetchYoutubeOembedTitle(
  youtubeId: string,
  opts?: { timeoutMs?: number }
): Promise<string | null> {
  const timeoutMs = opts?.timeoutMs ?? 2500;
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const url = `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(
      `https://www.youtube.com/watch?v=${youtubeId}`
    )}`;
    const res = await fetch(url, { signal: ctrl.signal, mode: 'cors' });
    if (!res.ok) return null;
    const data = (await res.json()) as { title?: unknown };
    return typeof data.title === 'string' && data.title.trim()
      ? data.title.trim()
      : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(t);
  }
}

async function pickFirstWorkingYoutubeId(
  candidates: string[],
  used: Set<string>
): Promise<string | null> {
  for (const id of candidates) {
    if (!id || used.has(id)) continue;
    // eslint-disable-next-line no-await-in-loop
    const ok = await isYoutubeVideoEmbeddableCached(id);
    if (ok) return id;
  }
  return null;
}

type StreamOption = {
  title: string;
  youtubeIds: string[];
};

function buildStreamOptions(original: { title: string; youtubeIds: string[] }): StreamOption[] {
  const seen = new Set(original.youtubeIds);
  const poolOpts: StreamOption[] = [];
  for (const p of STREAM_POOL) {
    if (!p.youtubeId || seen.has(p.youtubeId)) continue;
    seen.add(p.youtubeId);
    poolOpts.push({ title: p.title, youtubeIds: [p.youtubeId] });
  }
  return [
    { title: original.title, youtubeIds: original.youtubeIds },
    ...poolOpts,
  ];
}

function wrapOptionIndex(index: number, length: number): number {
  return ((index % length) + length) % length;
}

/** Pick next embed ID immediately (swap path — no oEmbed round-trips). */
function pickYoutubeIdForOption(
  opt: StreamOption,
  used: Set<string>,
  excludeId?: string
): string | null {
  const candidates = [...(opt.youtubeIds || []), ...BACKUP_YT_IDS];
  for (const id of candidates) {
    if (!id || used.has(id) || id === excludeId) continue;
    return id;
  }
  return null;
}

function createDockStreamIframe(
  cellIndex: number,
  optionIndex: number,
  yt: string,
  shownTitle: string
): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.src = youtubeSrc(yt, true);
  iframe.title = shownTitle;
  iframe.setAttribute('data-youtube-id', yt);
  iframe.setAttribute('data-stream-title', shownTitle);
  iframe.setAttribute('data-stream-idx', String(cellIndex));
  iframe.setAttribute('data-stream-opt', String(optionIndex));
  iframe.setAttribute('allow', STREAM_IFRAME_ALLOW);
  iframe.allowFullscreen = true;
  return iframe;
}

function setupMapAndStreams(
  parent: HTMLElement
): { mapApi: WorldMapApi; refreshLayout: () => void } {
  document
    .querySelectorAll(
      '#stream-expand-flyout, .stream-expand-viewport-root, .stream-expand-overlay'
    )
    .forEach((el) => el.remove());

  const fold = document.createElement('div');
  fold.className = 'monitor-fold';

  const mapSection = document.createElement('section');
  mapSection.className = 'world-map-section world-map-section--fold';
  const activeMigrationRoutes = MIGRATION_ROUTE_GROUPS.filter(
    (g) => getSeasonalState(g.id).visuallyActive
  );
  const migrationRouteChecks =
    activeMigrationRoutes.length > 0
      ? activeMigrationRoutes
          .map((g) => {
            const badge = getSeasonBadge(g.id);
            const badgeEl = badge
              ? `<span class="mig-season-badge">${escHtml(badge)}</span>`
              : '';
            return `
    <label class="map-filter-row map-filter-row--migration" title="${g.endpoints.start.title} → ${g.endpoints.end.title}">
      <input type="checkbox" class="map-mig-group-cb" data-mig-group-id="${g.id}" style="accent-color:${g.color}" checked />
      <span class="map-filter-migration-text">${g.label}</span>
      ${badgeEl}
    </label>
  `;
          })
          .join('')
      : `<p class="map-layer-empty">No migrations in season right now — try another time of year.</p>`;
  const migrationLegendDots =
    activeMigrationRoutes.length > 0
      ? activeMigrationRoutes
          .map(
            (g) =>
              `<span class="legend-mig-route" style="background:${g.color}" title="${g.label.replace(/"/g, '&quot;')}"></span>`
          )
          .join('')
      : '';
  const migrationLegendHtml =
    migrationLegendDots.length > 0
      ? `<span class="legend-migrations-row" title="In-season migrations">${migrationLegendDots}<span class="legend-migrations-label">Active</span></span>`
      : '';
  const otherMigrationChecks = MIGRATION_MAP_FEATURES.map(
    (f) => `
    <label title="${f.description.replace(/"/g, '&quot;')}">
      <input type="checkbox" class="map-mig-cb" data-mig-id="${f.id}" checked />
      ${f.title}
    </label>
  `
  ).join('');
  const migrationPinsBlock =
    MIGRATION_MAP_FEATURES.length > 0
      ? `
            <div class="map-layer-block">
              <div class="map-layer-block__label">Migrations — pins</div>
              <div class="map-layer-block__items">${otherMigrationChecks}</div>
            </div>`
      : '';
  const migrationRoutesSubsection =
    activeMigrationRoutes.length > 0
      ? `
            <details class="map-layer-subsection">
              <summary>Routes by species</summary>
              <div class="map-layer-subsection__content">${migrationRouteChecks}</div>
            </details>`
      : `<p class="map-layer-empty">No migrations in season right now — try another time of year.</p>`;
  mapSection.innerHTML = `
    <div class="map-stage map-stage--fullbleed">
      <div id="world-map-canvas" class="world-map-canvas world-map-canvas--fold" aria-label="World map"></div>
      <aside class="map-layers-overlay" aria-label="Map layers and location">
        <div class="map-layers-overlay__inner">
          <header class="map-layers-head">
            <span class="map-layers-title">Layers</span>
          </header>
          <div class="map-layers-scroll">
            <div class="map-layer-block map-layer-block--migrations">
              <div class="map-layer-block__label">Animal migrations</div>
              <div class="map-layer-block__items">
                <label class="map-filter-row">
                  <input type="checkbox" id="migration-master" checked />
                  Show all migration paths
                </label>
                ${migrationRoutesSubsection}
              </div>
            </div>
            ${migrationPinsBlock}
            <div class="map-layer-block">
              <div class="map-layer-block__label">Volcanoes & seismic</div>
              <label><input type="checkbox" id="layer-volcanoes" /> Active volcanoes & earthquakes (M4.5+ week)</label>
            </div>
            <div class="map-layer-block">
              <div class="map-layer-block__label">Tides</div>
              <div class="map-layer-block__items map-layer-block__items--tight">
                <label><input type="checkbox" id="layer-tides-noaa" /> NOAA US</label>
                <label><input type="checkbox" id="layer-tides-global" /> Global</label>
              </div>
            </div>
          </div>
          <footer class="map-layers-foot">
            <div class="map-layers-loc">
              <button type="button" class="btn-map-ov" id="btn-use-location">My location</button>
              <button type="button" class="btn-map-ov btn-map-ov--ghost" id="btn-clear-location">Clear</button>
            </div>
            <p class="map-layers-status" id="location-status"></p>
          </footer>
        </div>
      </aside>
      <div class="map-legend-bar" aria-hidden="true">
        ${migrationLegendHtml}
        <span class="legend-tree">Trees</span>
        <span class="legend-flower">Flowers</span>
        <span class="legend-crop">Crops</span>
        <span class="legend-tide">Tides US</span>
        <span class="legend-tide-global">Tides global</span>
        <span class="legend-volcano">Volcanoes</span>
        <span class="legend-seismic">Seismic</span>
        <span class="legend-you">You</span>
      </div>
      <aside class="migration-detail-panel" id="migration-detail-panel" aria-hidden="true">
        <div class="migration-detail-panel__inner">
          <header class="migration-detail-panel__header">
            <h3 id="mig-panel-title" class="migration-detail-panel__title"></h3>
            <button type="button" class="migration-detail-close" aria-label="Close migration details">×</button>
          </header>
          <div class="migration-detail-panel__scroll" id="mig-panel-body"></div>
        </div>
      </aside>
    </div>
  `;

  const streamsSection = document.createElement('div');
  streamsSection.className = 'streams-section';
  streamsSection.setAttribute('aria-label', 'Live streams');

  const streamDockHeader = document.createElement('div');
  streamDockHeader.className = 'stream-dock-header';
  streamDockHeader.innerHTML = `
    <span class="stream-dock-header-label">Livestreams</span>
    <button type="button" class="stream-dock-expand-section" aria-label="Expand livestream section for better visibility">Expand section</button>
  `;

  const streamDock = document.createElement('div');
  streamDock.className = 'stream-dock';
  streamDock.innerHTML = DOCK_STREAMS.map(
    (s, i) => `
    <div class="stream-dock-cell">
      <div class="stream-dock-head">
        <span class="stream-dock-label" title="${s.title.replace(/"/g, '&quot;')}">${s.title}</span>
        <div class="stream-dock-actions">
          <button type="button" class="stream-dock-swap" data-swap-stream="${i}" aria-label="Swap ${s.title.replace(/"/g, '&quot;')} to another livestream">Swap</button>
          <button type="button" class="stream-dock-expand" data-expand-stream="${i}" aria-label="Expand ${s.title.replace(/"/g, '&quot;')}">Expand</button>
        </div>
      </div>
      <div class="stream-dock-embed">
        <iframe
          src="${youtubeSrc(s.youtubeIds[0] || BACKUP_YT_IDS[0], true)}"
          title="${s.title.replace(/"/g, '&quot;')}"
          data-youtube-id="${(s.youtubeIds[0] || BACKUP_YT_IDS[0]).replace(/"/g, '&quot;')}"
          data-stream-title="${s.title.replace(/"/g, '&quot;')}"
          data-stream-idx="${i}"
          data-stream-opt="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        ></iframe>
      </div>
    </div>
  `
  ).join('');

  const streamExpandFlyout = document.createElement('div');
  streamExpandFlyout.id = 'stream-expand-flyout';
  streamExpandFlyout.className = 'stream-expand-flyout';
  streamExpandFlyout.setAttribute('dir', 'ltr');
  streamExpandFlyout.hidden = true;
  streamExpandFlyout.setAttribute('aria-hidden', 'true');
  streamExpandFlyout.setAttribute('role', 'region');
  streamExpandFlyout.setAttribute('aria-label', 'Expanded stream');
  streamExpandFlyout.innerHTML = `
    <div class="stream-expand-toolbar">
      <h2 id="stream-expand-title" class="stream-expand-title"></h2>
      <div class="stream-expand-actions">
        <button type="button" class="stream-expand-swap" aria-label="Swap expanded livestream">Swap</button>
        <button type="button" class="stream-expand-collapse" aria-label="Collapse expanded video">Collapse</button>
      </div>
    </div>
    <div class="stream-expand-embed">
      <iframe
        id="stream-expand-iframe"
        title=""
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
      ></iframe>
    </div>
  `;

  const heroResizeHandle = document.createElement('div');
  heroResizeHandle.className = 'hero-map-resize-handle';
  heroResizeHandle.setAttribute('role', 'separator');
  heroResizeHandle.setAttribute('aria-orientation', 'horizontal');
  heroResizeHandle.setAttribute('aria-label', 'Resize map height');

  streamsSection.appendChild(streamDockHeader);
  streamsSection.appendChild(streamDock);
  fold.appendChild(mapSection);
  fold.appendChild(streamsSection);
  fold.appendChild(heroResizeHandle);
  parent.appendChild(fold);
  document.body.appendChild(streamExpandFlyout);

  const expandTitle = streamExpandFlyout.querySelector(
    '#stream-expand-title'
  ) as HTMLElement;
  const expandEmbed = streamExpandFlyout.querySelector(
    '.stream-expand-embed'
  ) as HTMLElement;
  let expandIframe = streamExpandFlyout.querySelector(
    '#stream-expand-iframe'
  ) as HTMLIFrameElement;
  const expandSwapBtn = streamExpandFlyout.querySelector(
    '.stream-expand-swap'
  ) as HTMLButtonElement;
  const collapseBtn = streamExpandFlyout.querySelector(
    '.stream-expand-collapse'
  ) as HTMLButtonElement;
  let expandedPoolIndex = -1;
  let expandYtPlayer: YoutubeIFramePlayer | null = null;
  let expandYtRecoverAttempts = 0;
  let expandAttachGen = 0;

  function destroyExpandYtPlayer(): void {
    if (expandYtPlayer) {
      try {
        expandYtPlayer.destroy();
      } catch {
        // ignore teardown races
      }
      expandYtPlayer = null;
    }
  }

  /** YT.Player.destroy() removes the iframe; always insert a fresh one before loading. */
  function recreateExpandIframe(title: string, yt: string): HTMLIFrameElement {
    expandAttachGen += 1;
    destroyExpandYtPlayer();
    const iframe = document.createElement('iframe');
    iframe.id = 'stream-expand-iframe';
    iframe.title = title;
    iframe.setAttribute('allow', STREAM_IFRAME_ALLOW);
    iframe.allowFullscreen = true;
    iframe.setAttribute('data-youtube-id', yt);
    iframe.setAttribute('data-stream-title', title);
    iframe.src = youtubeSrc(yt, true);
    expandEmbed.replaceChildren(iframe);
    expandIframe = iframe;
    return iframe;
  }

  function buildExpandedPool(): { title: string; youtubeId: string }[] {
    const seen = new Set<string>();
    const out: { title: string; youtubeId: string }[] = [];
    // Prefer the configured pool.
    for (const p of STREAM_POOL) {
      if (!p.youtubeId || seen.has(p.youtubeId)) continue;
      seen.add(p.youtubeId);
      out.push({ title: p.title, youtubeId: p.youtubeId });
    }
    // Also include all configured dock stream IDs so everything is reachable.
    for (const s of DOCK_STREAMS) {
      for (const id of s.youtubeIds || []) {
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({ title: s.title, youtubeId: id });
      }
    }
    return out;
  }

  async function setExpandedToPoolIndex(startIndex: number): Promise<boolean> {
    const pool = buildExpandedPool();
    if (!pool.length) return false;
    const currentYt = expandIframe?.getAttribute('data-youtube-id') || '';

    for (let attempt = 0; attempt < pool.length; attempt++) {
      const idx = wrapOptionIndex(startIndex + attempt, pool.length);
      const item = pool[idx];
      const yt = pickYoutubeIdForOption(
        { title: item.title, youtubeIds: [item.youtubeId] },
        new Set<string>(),
        currentYt
      );
      if (!yt) continue;

      let title = item.title;
      if (!title || title.toLowerCase() === 'livestream' || yt !== item.youtubeId) {
        // eslint-disable-next-line no-await-in-loop
        const t = await fetchYoutubeOembedTitle(yt);
        if (t) title = t;
      }
      expandedPoolIndex = idx;
      expandTitle.textContent = title;
      recreateExpandIframe(title, yt);
      return true;
    }
    return false;
  }

  const MAX_EXPAND_YT_RECOVER = 36;

  async function attachExpandIframePlayer(): Promise<void> {
    const gen = ++expandAttachGen;
    const iframe = expandIframe;
    if (!iframe?.isConnected || !iframe.src?.includes('/embed/')) return;
    try {
      await ensureYoutubeIframeApiLoaded();
      if (gen !== expandAttachGen) return;
      const PlayerCtor = (window as unknown as { YT?: { Player?: YoutubePlayerCtor } }).YT
        ?.Player;
      if (!PlayerCtor || !iframe.isConnected) return;

      expandYtPlayer = new PlayerCtor(iframe, {
        events: {
          onReady: () => {
            if (gen !== expandAttachGen) return;
            expandYtRecoverAttempts = 0;
          },
          onError: () => {
            if (gen !== expandAttachGen) return;
            if (!streamExpandFlyout.hidden) expandYtRecoverAttempts += 1;
            if (expandYtRecoverAttempts > MAX_EXPAND_YT_RECOVER) return;
            void setExpandedToPoolIndex(expandedPoolIndex + 1).then(() =>
              attachExpandIframePlayer()
            );
          },
        },
      });
      const live = expandEmbed.querySelector('#stream-expand-iframe');
      if (live instanceof HTMLIFrameElement) expandIframe = live;
    } catch {
      // API or player init failed — embed may still autoplay normally
    }
  }

  function closeStreamExpand(): void {
    expandAttachGen += 1;
    destroyExpandYtPlayer();
    expandYtRecoverAttempts = 0;
    streamExpandFlyout.hidden = true;
    streamExpandFlyout.setAttribute('aria-hidden', 'true');
    if (expandIframe?.isConnected) {
      expandIframe.removeAttribute('src');
      expandIframe.title = '';
    }
    document.body.classList.remove('stream-expand-open');
    streamExpandFlyout.removeAttribute('style');
  }

  function openStreamExpand(index: number): void {
    const s = DOCK_STREAMS[index];
    if (!s) return;
    const cellIframe = streamDock.querySelector<HTMLIFrameElement>(
      `iframe[data-stream-idx="${index}"]`
    );
    const shownTitle =
      cellIframe?.getAttribute('data-stream-title') || s.title;
    const yt =
      cellIframe?.getAttribute('data-youtube-id') ||
      s.youtubeIds[0] ||
      BACKUP_YT_IDS[0];
    expandTitle.textContent = shownTitle;
    recreateExpandIframe(shownTitle, yt);
    // Set pool index so "Swap" continues from current video.
    const pool = buildExpandedPool();
    const pos = pool.findIndex((p) => p.youtubeId === yt);
    expandedPoolIndex = pos >= 0 ? pos : -1;
    streamExpandFlyout.hidden = false;
    streamExpandFlyout.setAttribute('aria-hidden', 'false');
    document.body.classList.add('stream-expand-open');
    void attachExpandIframePlayer();
  }

  collapseBtn.addEventListener('click', closeStreamExpand);
  expandSwapBtn?.addEventListener('click', () => {
    const next = expandedPoolIndex + 1;
    void setExpandedToPoolIndex(next).then((ok) => {
      if (ok) void attachExpandIframePlayer();
      else if (expandTitle.textContent) {
        const prev = expandTitle.textContent;
        expandTitle.textContent = 'No streams available';
        window.setTimeout(() => {
          if (expandTitle.textContent === 'No streams available') {
            expandTitle.textContent = prev;
          }
        }, 2200);
      }
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !streamExpandFlyout.hidden) closeStreamExpand();
    if (e.key === 'Escape' && migPanel?.classList.contains('migration-detail-panel--open'))
      closeMigrationPanel();
  });

  streamDock.querySelectorAll('[data-expand-stream]').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const i = parseInt((btn as HTMLElement).dataset.expandStream || '0', 10);
      openStreamExpand(i);
    });
  });

  function getActiveYoutubeIds(exceptCellIndex?: number): Set<string> {
    const ids = new Set<string>();
    streamDock
      .querySelectorAll<HTMLIFrameElement>('iframe[data-youtube-id][data-stream-idx]')
      .forEach((f) => {
        const idx = parseInt(f.getAttribute('data-stream-idx') || '-1', 10);
        if (typeof exceptCellIndex === 'number' && idx === exceptCellIndex) return;
        const id = f.getAttribute('data-youtube-id');
        if (id) ids.add(id);
      });
    return ids;
  }

  async function applyDockCellStream(
    cellIndex: number,
    optionIndex: number,
    opt: StreamOption,
    yt: string
  ): Promise<void> {
    const oldIframe = streamDock.querySelector<HTMLIFrameElement>(
      `iframe[data-stream-idx="${cellIndex}"]`
    );
    const cell = oldIframe?.closest('.stream-dock-cell') as HTMLElement | null;
    const embed = oldIframe?.parentElement;
    const label = cell?.querySelector('.stream-dock-label') as HTMLElement | null;
    const expandBtn = cell?.querySelector('.stream-dock-expand') as HTMLButtonElement | null;
    if (!oldIframe || !embed || !label || !expandBtn) return;

    destroyDockYtPlayerFor(oldIframe);

    const preferred = new Set(opt.youtubeIds || []);
    let shownTitle = opt.title;
    if (!shownTitle || shownTitle.toLowerCase() === 'livestream' || !preferred.has(yt)) {
      const t = await fetchYoutubeOembedTitle(yt);
      if (t) shownTitle = t;
    }

    const iframe = createDockStreamIframe(cellIndex, optionIndex, yt, shownTitle);
    embed.replaceChildren(iframe);

    label.textContent = shownTitle;
    label.setAttribute('title', shownTitle);
    expandBtn.setAttribute('aria-label', `Expand ${shownTitle}`);

    void attachDockIframePlayer(cellIndex);
  }

  const dockSwapInFlight = new Set<number>();

  /** Advance dock cell through swap options until a different stream loads. */
  async function advanceDockCellStream(
    cellIndex: number,
    fromOptionIndex: number
  ): Promise<boolean> {
    if (dockSwapInFlight.has(cellIndex)) return false;
    dockSwapInFlight.add(cellIndex);

    try {
      const s = DOCK_STREAMS[cellIndex];
      if (!s) return false;
      const iframe = streamDock.querySelector<HTMLIFrameElement>(
        `iframe[data-stream-idx="${cellIndex}"]`
      );
      const cell = iframe?.closest('.stream-dock-cell') as HTMLElement | null;
      const label = cell?.querySelector('.stream-dock-label') as HTMLElement | null;
      if (!iframe || !label) return false;

      const opts = buildStreamOptions(s);
      if (!opts.length) return false;

      const currentYt = iframe.getAttribute('data-youtube-id') || '';
      const usedElsewhere = getActiveYoutubeIds(cellIndex);

      const tryPasses: Set<string>[] = [usedElsewhere, new Set<string>()];
      for (const used of tryPasses) {
        for (let attempt = 0; attempt < opts.length; attempt++) {
          const optionIndex = fromOptionIndex + attempt;
          const norm = wrapOptionIndex(optionIndex, opts.length);
          const opt = opts[norm];
          const yt = pickYoutubeIdForOption(opt, used, currentYt);
          if (!yt) continue;
          // eslint-disable-next-line no-await-in-loop
          await applyDockCellStream(cellIndex, norm, opt, yt);
          return true;
        }
      }

      return false;
    } finally {
      dockSwapInFlight.delete(cellIndex);
    }
  }

  function flashStreamUnavailable(label: HTMLElement): void {
    const prev = label.textContent || '';
    label.textContent = 'No streams available';
    window.setTimeout(() => {
      if (label.textContent === 'No streams available') label.textContent = prev;
    }, 2200);
  }

  const dockYtPlayers = new WeakMap<HTMLIFrameElement, YoutubeIFramePlayer>();
  const dockRecoverAttempts = new Map<number, number>();
  const dockRecoverInFlight = new Set<number>();

  const MAX_DOCK_YT_RECOVER = 40;

  function destroyDockYtPlayerFor(iframe: HTMLIFrameElement): void {
    const p = dockYtPlayers.get(iframe);
    if (!p) return;
    try {
      p.destroy();
    } catch {
      //
    }
    dockYtPlayers.delete(iframe);
  }

  async function attachDockIframePlayer(cellIndex: number): Promise<void> {
    try {
      await ensureYoutubeIframeApiLoaded();
      const iframe = streamDock.querySelector<HTMLIFrameElement>(
        `iframe[data-stream-idx="${cellIndex}"]`
      );
      const srcUrl = iframe?.src;
      if (!iframe || !srcUrl?.includes('/embed/')) return;

      const PlayerCtor = (window as unknown as { YT?: { Player?: YoutubePlayerCtor } }).YT
        ?.Player;
      if (!PlayerCtor) return;

      destroyDockYtPlayerFor(iframe);

      const player = new PlayerCtor(iframe, {
        events: {
          onReady: () => {
            dockRecoverAttempts.delete(cellIndex);
          },
          onError: () => {
            if (dockRecoverInFlight.has(cellIndex)) return;
            const n = (dockRecoverAttempts.get(cellIndex) || 0) + 1;
            if (n > MAX_DOCK_YT_RECOVER) return;
            dockRecoverAttempts.set(cellIndex, n);
            dockRecoverInFlight.add(cellIndex);
            void (async () => {
              try {
                const currentOpt = parseInt(
                  iframe.getAttribute('data-stream-opt') || '0',
                  10
                );
                await advanceDockCellStream(cellIndex, currentOpt + 1);
                await enforceUniqueDock();
              } finally {
                dockRecoverInFlight.delete(cellIndex);
              }
            })();
          },
        },
      });
      dockYtPlayers.set(iframe, player);
    } catch {
      //
    }
  }

  streamDock.querySelectorAll('[data-swap-stream]').forEach((btn) => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const i = parseInt((btn as HTMLElement).dataset.swapStream || '0', 10);
      const iframe = streamDock.querySelector<HTMLIFrameElement>(
        `iframe[data-stream-idx="${i}"]`
      );
      const label = iframe
        ?.closest('.stream-dock-cell')
        ?.querySelector('.stream-dock-label') as HTMLElement | null;
      const currentOpt = parseInt(iframe?.getAttribute('data-stream-opt') || '0', 10);
      void advanceDockCellStream(i, currentOpt + 1).then((ok) => {
        if (!ok && label) flashStreamUnavailable(label);
      });
    });
  });

  async function enforceUniqueDock(): Promise<void> {
    const frames = Array.from(
      streamDock.querySelectorAll<HTMLIFrameElement>('iframe[data-youtube-id][data-stream-idx]')
    );
    const used = new Set<string>();
    for (let idx = 0; idx < frames.length; idx++) {
      const frame = frames[idx];
      const cellIndex = parseInt(frame.getAttribute('data-stream-idx') || String(idx), 10);
      const current = frame.getAttribute('data-youtube-id') || '';
      if (current && !used.has(current)) {
        used.add(current);
        continue;
      }
      // Duplicate (or blank): advance within that cell’s cycle until unique.
      const currentOpt = parseInt(frame.getAttribute('data-stream-opt') || '0', 10);
      // eslint-disable-next-line no-await-in-loop
      await advanceDockCellStream(cellIndex, currentOpt + 1);
      const now = frame.getAttribute('data-youtube-id') || '';
      if (now) used.add(now);
    }
  }

  // oEmbed catches removed/private IDs; iframe API `onError` cycles out playable-but-dead archives
  // (“live stream recording is not available”) that still pass oEmbed.
  const used = new Set<string>();
  const iframes = Array.from(
    streamDock.querySelectorAll<HTMLIFrameElement>(
      'iframe[data-youtube-id][data-stream-idx]'
    )
  );
  iframes.forEach((f) => {
    const id = f.getAttribute('data-youtube-id');
    if (id) used.add(id);
  });
  void (async () => {
    for (let idx = 0; idx < DOCK_STREAMS.length; idx++) {
      const s = DOCK_STREAMS[idx];
      const frame = iframes.find((f) => f.getAttribute('data-stream-idx') === String(idx));
      if (!s || !frame) continue;
      const current = frame.getAttribute('data-youtube-id') || '';
      // eslint-disable-next-line no-await-in-loop
      const ok = current ? await isYoutubeVideoEmbeddableCached(current) : false;
      if (ok) continue;

      const next = await pickFirstWorkingYoutubeId(
        [...(s.youtubeIds || []), ...BACKUP_YT_IDS],
        used
      );
      if (!next) continue;
      used.add(next);
      const shownTitle =
        frame.getAttribute('data-stream-title') ||
        (await fetchYoutubeOembedTitle(next)) ||
        s.title;
      const embed = frame.parentElement;
      if (embed) {
        destroyDockYtPlayerFor(frame);
        embed.replaceChildren(createDockStreamIframe(idx, 0, next, shownTitle));
      }
      const cell = embed?.closest('.stream-dock-cell') as HTMLElement | null;
      const label = cell?.querySelector('.stream-dock-label') as HTMLElement | null;
      const expandBtn = cell?.querySelector('.stream-dock-expand') as HTMLButtonElement | null;
      if (label) {
        label.textContent = shownTitle;
        label.setAttribute('title', shownTitle);
      }
      if (expandBtn) expandBtn.setAttribute('aria-label', `Expand ${shownTitle}`);
    }

    await enforceUniqueDock();

    await ensureYoutubeIframeApiLoaded();
    for (let i = 0; i < DOCK_STREAMS.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await attachDockIframePlayer(i);
    }
  })();

  const expandSectionBtn = streamDockHeader.querySelector(
    '.stream-dock-expand-section'
  ) as HTMLButtonElement;
  expandSectionBtn?.addEventListener('click', () => {
    const expanded = fold.classList.toggle('monitor-fold--streams-expanded');
    expandSectionBtn.textContent = expanded ? 'Collapse section' : 'Expand section';
    expandSectionBtn.setAttribute(
      'aria-label',
      expanded
        ? 'Collapse livestream section'
        : 'Expand livestream section for better visibility'
    );
    mapApi.invalidateSize();
  });

  const migPanel = mapSection.querySelector(
    '#migration-detail-panel'
  ) as HTMLElement | null;
  const migBody = mapSection.querySelector('#mig-panel-body') as HTMLElement | null;
  const migTitle = mapSection.querySelector('#mig-panel-title') as HTMLElement | null;
  let migPanelAbort: AbortController | null = null;

  function closeMigrationPanel(): void {
    migPanelAbort?.abort();
    migPanel?.classList.remove('migration-detail-panel--open');
    migPanel?.setAttribute('aria-hidden', 'true');
  }

  async function openMigrationDetailPanel(groupId: string): Promise<void> {
    migPanelAbort?.abort();
    migPanelAbort = new AbortController();
    const sig = migPanelAbort.signal;
    const g = getMigrationGroupById(groupId);
    if (!g || !migBody || !migTitle || !migPanel) return;
    migTitle.textContent = g.label;
    migPanel.classList.add('migration-detail-panel--open');
    migPanel.setAttribute('aria-hidden', 'false');
    migBody.innerHTML = '<p class="mig-panel-muted"><em>Loading…</em></p>';

    const seasonal = getSeasonalState(groupId);
    const statusLine =
      seasonal.activity === 'off-season'
        ? 'Currently: <strong>Off season</strong>'
        : seasonal.activity === 'peak'
          ? 'Currently: <strong>Peak migration</strong>'
          : seasonal.activity === 'approaching'
            ? 'Currently: <strong>Ramping up</strong>'
            : seasonal.activity === 'complete'
              ? 'Currently: <strong>Winding down</strong>'
              : 'Currently: <strong>Active</strong>';

    let photo = '';
    if (g.detail?.inatTaxonId) {
      const url = await fetchTaxonDefaultPhotoUrl(g.detail.inatTaxonId);
      if (!sig.aborted && url?.startsWith('https://')) {
        const safe = url.replace(/"/g, '');
        photo = `<img class="mig-panel-photo" src="${safe}" alt="" />`;
      }
    }

    let inatSection = '';
    if (g.detail?.inatTaxonId) {
      try {
        const bb = routeWaypointsBounds(g.waypoints, 6);
        const pins = await fetchObservationsByTaxonInBounds(
          g.detail.inatTaxonId,
          bb.nelat,
          bb.nelng,
          bb.swlat,
          bb.swlng,
          { signal: sig, perPage: 10 }
        );
        if (!sig.aborted) {
          if (pins.length) {
            inatSection = `<h4 class="mig-panel-h4">Research-grade iNat (corridor)</h4><ul class="mig-panel-inat-list">${pins
              .map(
                (p) =>
                  `<li><a href="${escHtml(p.url)}" target="_blank" rel="noopener">${escHtml(p.name)}</a>${p.observedOn ? ` · ${escHtml(p.observedOn)}` : ''}</li>`
              )
              .join('')}</ul>`;
          } else {
            inatSection =
              '<p class="mig-panel-muted">No observations in corridor bbox — zoom map or check iNat.</p>';
          }
        }
      } catch {
        if (!sig.aborted) {
          inatSection =
            '<p class="mig-panel-muted">Could not load iNaturalist.</p>';
        }
      }
    }

    if (sig.aborted) return;
    const fact = g.detail?.funFact
      ? `<p class="mig-panel-fact"><strong>Fun fact:</strong> ${escHtml(g.detail.funFact)}</p>`
      : '';
    const lore = g.detail?.lore
      ? `<p class="mig-panel-lore">${escHtml(g.detail.lore)}</p>`
      : '';
    migBody.innerHTML = `
      ${photo}
      <p class="mig-panel-status">${statusLine}</p>
      <p class="mig-panel-now"><strong>Approx. now:</strong> ${escHtml(seasonal.nowLabel)}</p>
      <p class="mig-panel-sub">${escHtml(seasonal.statusLine)}</p>
      ${fact}
      ${lore}
      ${inatSection}
      <p class="mig-panel-disclaimer">Seasonal positions are estimates, not live tracking.</p>
    `;
  }

  migPanel
    ?.querySelector('.migration-detail-close')
    ?.addEventListener('click', closeMigrationPanel);

  const canvas = mapSection.querySelector('#world-map-canvas') as HTMLElement;
  const saved = loadSavedLocation();
  const mapApi = initWorldMap(canvas, {
    savedLocation: saved ? { lat: saved.lat, lng: saved.lng } : null,
    onMigrationPanel: ({ groupId }) => {
      void openMigrationDetailPanel(groupId);
    },
  });

  // Drag-to-resize map height (desktop). Uses CSS var on `.monitor-fold`.
  {
    const MIN_H = 800;
    const MAX_H = 1600;
    let startY = 0;
    let startH = 0;
    let raf = 0;
    const applyH = (h: number) => {
      const clamped = Math.max(MIN_H, Math.min(MAX_H, Math.round(h)));
      fold.style.setProperty('--map-hero-height', `${clamped}px`);
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => mapApi.invalidateSize());
    };

    heroResizeHandle.addEventListener('pointerdown', (e) => {
      if (fold.classList.contains('monitor-fold--streams-expanded')) return;
      startY = e.clientY;
      startH = mapSection.getBoundingClientRect().height;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      e.preventDefault();
    });
    heroResizeHandle.addEventListener('pointermove', (e) => {
      if (!(e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)) return;
      const dy = e.clientY - startY;
      applyH(startH + dy);
    });
    heroResizeHandle.addEventListener('pointerup', (e) => {
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    });
  }

  const locStatus = mapSection.querySelector('#location-status');
  function updateLocationLabel(): void {
    const s = loadSavedLocation();
    if (locStatus) {
      locStatus.textContent = s
        ? `Saved: ${s.lat.toFixed(3)}, ${s.lng.toFixed(3)}`
        : 'No saved location — default world view';
    }
  }
  updateLocationLabel();

  mapSection.querySelector('#btn-use-location')?.addEventListener('click', () => {
    const btn = mapSection.querySelector('#btn-use-location') as HTMLButtonElement;
    btn.disabled = true;
    requestBrowserLocation()
      .then(({ lat, lng }) => {
        saveUserLocation(lat, lng);
        mapApi.setUserLocation(lat, lng);
        mapApi.flyTo(lat, lng, 8);
        mapApi.invalidateSize();
        updateLocationLabel();
      })
      .catch(() => {
        if (locStatus) locStatus.textContent = 'Location denied or unavailable.';
      })
      .finally(() => {
        btn.disabled = false;
      });
  });
  mapSection.querySelector('#btn-clear-location')?.addEventListener('click', () => {
    clearSavedLocation();
    mapApi.setUserLocation(null, null);
    mapApi.flyTo(22, 10, 2);
    updateLocationLabel();
  });

  const migrationMasterCb = mapSection.querySelector('#migration-master') as HTMLInputElement;
  const volcanoCb = mapSection.querySelector('#layer-volcanoes') as HTMLInputElement;
  const migrationDetails = mapSection.querySelector('.map-layer-subsection') as HTMLDetailsElement | null;
  const migrationSubsection = mapSection.querySelector('.map-layer-subsection__content');
  function setMigrationSubsectionDisabled(disabled: boolean): void {
    migrationSubsection?.querySelectorAll<HTMLInputElement>('.map-mig-group-cb').forEach((cb) => {
      cb.disabled = disabled;
    });
  }
  migrationMasterCb.addEventListener('change', () => {
    const on = migrationMasterCb.checked;
    mapApi.setMigrationMaster(on);
    setMigrationSubsectionDisabled(!on);
    if (migrationDetails) migrationDetails.open = on;
  });
  setMigrationSubsectionDisabled(!migrationMasterCb.checked);

  mapSection.querySelectorAll('.map-mig-group-cb').forEach((el) => {
    el.addEventListener('change', () => {
      const inp = el as HTMLInputElement;
      const id = inp.dataset.migGroupId;
      if (id) mapApi.setMigrationGroup(id, inp.checked);
    });
  });
  mapSection.querySelectorAll('.map-mig-cb').forEach((el) => {
    el.addEventListener('change', () => {
      const inp = el as HTMLInputElement;
      const id = inp.dataset.migId;
      if (id) mapApi.setMigration(id, inp.checked);
    });
  });
  const tNoaa = mapSection.querySelector('#layer-tides-noaa') as HTMLInputElement;
  const tGlob = mapSection.querySelector('#layer-tides-global') as HTMLInputElement;
  tNoaa.addEventListener('change', () => mapApi.setTidesNoaa(tNoaa.checked));
  tGlob.addEventListener('change', () =>
    mapApi.setTidesGlobal(tGlob.checked)
  );
  volcanoCb.addEventListener('change', () => mapApi.setVolcanoes(volcanoCb.checked));

  const mapState = mapApi.getState();
  migrationMasterCb.checked = mapState.migrationMasterOn;
  volcanoCb.checked = mapState.volcanoes;
  setMigrationSubsectionDisabled(!mapState.migrationMasterOn);

  function refreshLayout(): void {
    mapApi.invalidateSize();
  }

  refreshLayout();
  const ro = new ResizeObserver(() => mapApi.invalidateSize());
  ro.observe(fold);
  window.addEventListener('resize', refreshLayout);

  window.addEventListener(
    'beforeunload',
    () => {
      ro.disconnect();
      mapApi.destroy();
    },
    { once: true }
  );

  return { mapApi, refreshLayout };
}

export function renderApp(): void {
  const app = document.getElementById('app');
  if (!app) return;

  const today = format(new Date(), 'EEEE, MMMM d, yyyy');

  app.innerHTML = `
    <header class="soft-header">
      <div>
        <h1>EarthMonitor</h1>
      </div>
      <div class="date-display">${today}</div>
    </header>
    <div id="main-content"></div>
  `;

  const main = document.getElementById('main-content');
  if (!main) return;

  setupMapAndStreams(main);

  const grid = document.createElement('div');
  grid.className = 'panel-grid';
  grid.id = 'panel-grid';
  main.appendChild(grid);

  type CardRenderer = (container: HTMLElement) => void;
  const cardRenderers: CardRenderer[] = [
    (c) => renderThreatRow(c, { excludeMother: true }),
    renderMotherNatureCard,
    renderMoonTarotCard,
    renderHoroscopeCard,
    renderMeteorsCard,
  ];
  cardRenderers.forEach((render) => render(grid));
}
